import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ShieldCheck, Briefcase, Crown, UserCheck, LogIn, Eye, EyeOff, KeyRound } from 'lucide-react';

const ROLES = [
  { id: 'applicant', label: 'Applicant', icon: UserCheck },
  { id: 'nokma', label: 'Nokma Sani', icon: Crown },
  { id: 'jakrikra', label: 'Jakrikra', icon: Briefcase },
  { id: 'admin', label: 'Administrator', icon: ShieldCheck },
];

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState('applicant');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(mobile)) { toast.error('Enter a valid 10-digit mobile number'); return; }
    if (!password) { toast.error('Please enter your password'); return; }
    setSubmitting(true);
    const res = await login({ role, mobile, password });
    setSubmitting(false);
    if (!res.ok) { toast.error(res.error || 'Login failed'); return; }
    toast.success(`Welcome, ${res.user.name}`);
    const dest = { admin: '/admin', jakrikra: '/jakrikra', nokma: '/nokma', applicant: '/applicant' }[res.user.role];
    navigate(dest);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-1 flex items-center justify-center py-12 px-4 tribal-pattern">
        <Card className="w-full max-w-md border-border shadow-warm">
          <CardHeader className="text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-2">
              <LogIn className="h-6 w-6" />
            </div>
            <CardTitle className="font-serif text-2xl">Sign in</CardTitle>
            <CardDescription>Access your Nokma Sani Union account</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={role} onValueChange={setRole} className="mb-5">
              <TabsList className="grid grid-cols-4 w-full">
                {ROLES.map((r) => (
                  <TabsTrigger key={r.id} value={r.id} className="text-xs flex flex-col gap-1 h-14">
                    <r.icon className="h-4 w-4" />
                    <span>{r.label.split(' ')[0]}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
              {ROLES.map((r) => (
                <TabsContent key={r.id} value={r.id} className="mt-3">
                  <div className="text-sm text-muted-foreground text-center">Sign in as <span className="font-medium text-foreground">{r.label}</span></div>
                </TabsContent>
              ))}
            </Tabs>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label htmlFor="mobile">Mobile Number</Label>
                <Input
                  id="mobile"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="10-digit mobile number"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                  className="mt-1.5"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  {(role === 'applicant' || role === 'nokma') && (
                    <button type="button" onClick={() => setForgotOpen(true)} className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                      <KeyRound className="h-3 w-3" /> Forgot password?
                    </button>
                  )}
                </div>
                <div className="relative mt-1.5">
                  <Input
                    id="password"
                    type={showPwd ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((s) => !s)}
                    aria-label={showPwd ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground"
                  >
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground">
                {submitting ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>

            <div className="mt-6 text-sm text-center text-muted-foreground">
              New here? <Link to="/register" className="text-primary font-medium hover:underline">Create an account</Link>
            </div>
          </CardContent>
        </Card>
      </div>

      <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} initialRole={role === 'admin' || role === 'jakrikra' ? 'applicant' : role} />
    </div>
  );
};

const ForgotPasswordDialog = ({ open, onOpenChange, initialRole }) => {
  const [role, setRole] = useState(initialRole || 'applicant');
  const [mobile, setMobile] = useState('');
  const [verification, setVerification] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => { if (open) setRole(initialRole || 'applicant'); }, [open, initialRole]);

  const reset = () => {
    setMobile(''); setVerification(''); setNewPassword(''); setConfirmPwd(''); setShowPwd(false);
  };

  const submit = async () => {
    if (!/^\d{10}$/.test(mobile)) { toast.error('Enter valid 10-digit mobile'); return; }
    if (!verification) { toast.error('Verification value required'); return; }
    if (newPassword.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (newPassword !== confirmPwd) { toast.error('Passwords do not match'); return; }
    setSubmitting(true);
    try {
      await api.post('/auth/forgot-password', { role, mobile, verification, newPassword });
      toast.success('Password reset successful. You can now sign in.');
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally {
      setSubmitting(false);
    }
  };

  const verificationLabel = role === 'applicant' ? 'Aadhar Number (12 digits)' : 'Nokma Number';
  const verificationPlaceholder = role === 'applicant' ? '12-digit Aadhar' : 'e.g. NK-101';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-serif flex items-center gap-2"><KeyRound className="h-5 w-5 text-primary" /> Reset Password</DialogTitle>
          <DialogDescription>
            Reset password for Applicant or Nokma Sani. For Administrator and Jakrikra, please contact the Council Administrator.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Tabs value={role} onValueChange={setRole}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="applicant">Applicant</TabsTrigger>
              <TabsTrigger value="nokma">Nokma Sani</TabsTrigger>
            </TabsList>
          </Tabs>

          <div>
            <Label>Registered Mobile</Label>
            <Input inputMode="numeric" maxLength={10} value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))} className="mt-1.5" />
          </div>
          <div>
            <Label>{verificationLabel}</Label>
            <Input value={verification} onChange={(e) => setVerification(e.target.value)} placeholder={verificationPlaceholder} className="mt-1.5" />
            <div className="text-xs text-muted-foreground mt-1">Used to verify your identity.</div>
          </div>
          <div>
            <Label>New Password</Label>
            <div className="relative mt-1.5">
              <Input type={showPwd ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="pr-10" />
              <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground">
                {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label>Confirm New Password</Label>
            <Input type={showPwd ? 'text' : 'password'} value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} className="mt-1.5" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-primary hover:bg-primary/90">
            {submitting ? 'Resetting...' : 'Reset password'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Login;
